import { useCallback, useRef, useState } from 'react';
import {
    Card,
    Tag,
    Row,
    Col,
    Popconfirm,
    message,
    Tooltip,
    Button,
    Divider,
    Descriptions,
} from 'antd';
import { DeleteOutlined, RedoOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import http from '../../http';
import ListView from '../../components/ListView/ListView';

const ORDERING_FIELDS = [
    { id: 'name', name: 'Name (A-Z)' },
    { id: '-name', name: 'Name (Z-A)' },
    { id: 'device_number', name: 'Device # (Asc)' },
    { id: '-device_number', name: 'Device # (Desc)' },
    { id: '-created', name: 'Registered (Newest)' },
    { id: 'created', name: 'Registered (Oldest)' },
];

const getStatus = (record) =>
    record.inactive ? { label: 'Blocked', color: 'red' } : { label: 'Active', color: 'green' };

const fmtDate = (v) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-');

// ListView expects { results, count }. Backend returns a plain array, so normalize.
const fetchTerminalDevices = (params) =>
    http.get('/api/terminal/devices/', { params }).then((resp) => {
        const data = resp.data;
        if (Array.isArray(data)) return { results: data, count: data.length };
        return { results: data?.results ?? [], count: data?.count ?? 0 };
    });

const TerminalDeviceCard = ({ record, onSetInactive, updating }) => {
    const status = getStatus(record);
    const blocked = !!record.inactive;

    const unregistered = !!record.unregistered_at;

    const fields = [
        { label: 'OS', value: record.os },
        { label: 'OS Version', value: record.os_version },
        { label: 'CPU', value: record.cpu_brand },
        { label: 'Registered', value: fmtDate(record.created) },
    ];
    if (unregistered) {
        fields.push({ label: 'Unregistered', value: fmtDate(record.unregistered_at) });
    }

    return (
        <Card
            hoverable
            style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
            styles={{
                body: { flex: 1, display: 'flex', flexDirection: 'column', padding: '16px' },
            }}
        >
            <div style={{ flexShrink: 0 }}>
                <Row align='middle' wrap={false} style={{ minHeight: 32 }}>
                    <Col flex='auto' style={{ minWidth: 0 }}>
                        <Tooltip title={record.name} placement='bottomLeft'>
                            <div
                                style={{
                                    fontWeight: 'bold',
                                    fontSize: '16px',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {record.name || `Device ${record.id}`}
                            </div>
                        </Tooltip>
                    </Col>
                    <Col flex='0 0 auto'>
                        {blocked ? (
                            <Popconfirm
                                title='Activate this device?'
                                okText='Yes'
                                cancelText='No'
                                onConfirm={() => onSetInactive(record, 0)}
                            >
                                <Tooltip title='Activate'>
                                    <Button
                                        type='text'
                                        icon={<RedoOutlined />}
                                        loading={updating}
                                    />
                                </Tooltip>
                            </Popconfirm>
                        ) : (
                            <Popconfirm
                                title='Block this device?'
                                description='Blocks all API access and re-registration.'
                                okText='Yes'
                                cancelText='No'
                                onConfirm={() => onSetInactive(record, 1)}
                            >
                                <Tooltip title='Block'>
                                    <Button
                                        type='text'
                                        danger
                                        icon={<DeleteOutlined />}
                                        loading={updating}
                                    />
                                </Tooltip>
                            </Popconfirm>
                        )}
                    </Col>
                </Row>
            </div>

            <Divider style={{ margin: '12px 0' }} />

            <div style={{ flex: 1, overflow: 'hidden' }}>
                <Descriptions column={1} size='small' layout='horizontal'>
                    {fields.map((f) => (
                        <Descriptions.Item key={f.label} label={f.label}>
                            <Tooltip title={f.value}>
                                <span
                                    style={{
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {f.value || f.value === 0 ? f.value : '-'}
                                </span>
                            </Tooltip>
                        </Descriptions.Item>
                    ))}
                </Descriptions>
            </div>

            <div
                style={{ flexShrink: 0, display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}
            >
                <Tag color={status.color}>{status.label}</Tag>
                {unregistered && <Tag color='orange'>Unregistered</Tag>}
            </div>
        </Card>
    );
};

const TerminalDeviceListPage = () => {
    const listRef = useRef(null);
    const [updatingId, setUpdatingId] = useState(null);

    const setInactive = useCallback((record, inactive) => {
        setUpdatingId(record.id);
        http.patch(`/api/terminal/devices/${record.id}/`, { inactive })
            .then(() => {
                message.success(inactive ? 'Device blocked' : 'Device activated');
                listRef.current?.refetch();
            })
            .catch(() => message.error('Update failed'))
            .finally(() => setUpdatingId(null));
    }, []);

    return (
        <ListView
            ref={listRef}
            title='Terminal Devices'
            fetchFn={fetchTerminalDevices}
            orderingFields={ORDERING_FIELDS}
            showSearch
            grid={{ xs: 24, sm: 24, md: 12, lg: 8, xl: 8, xxl: 6 }}
            emptyState={{
                title: 'No devices',
                description: 'There are no terminal devices to display.',
            }}
            renderItem={({ item }) => (
                <TerminalDeviceCard
                    key={item.id}
                    record={item}
                    onSetInactive={setInactive}
                    updating={updatingId === item.id}
                />
            )}
        />
    );
};

export default TerminalDeviceListPage;
